import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { OfficeKey } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';

const OFFICE_LABELS: Record<OfficeKey, string> = {
  buenosAires: 'Office Buenos Aires',
  cordoba: 'Office Córdoba',
  brasil: 'Office Brasil',
  colombia: 'Office Colombia',
  sinOffice: 'Sin office (remotos)',
};

const OFFICE_ORDER: OfficeKey[] = ['buenosAires', 'cordoba', 'brasil', 'colombia', 'sinOffice'];

type Offices = Record<OfficeKey, string[]>;

const EMPTY: Offices = { buenosAires: [], cordoba: [], brasil: [], colombia: [], sinOffice: [] };

/**
 * Panel admin: qué emails son Office Managers de cada oficina.
 * Guarda en ConfigTable (OFFICE_MANAGERS_GOVERNANCE); cada OM ve solo su oficina.
 */
export function OfficeManagerGovernancePanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const { data: gov, isLoading } = trpc.officeManager.getGovernance.useQuery(undefined, { enabled: open });
  const [offices, setOffices] = useState<Offices>(EMPTY);
  const [drafts, setDrafts] = useState<Record<OfficeKey, string>>({
    buenosAires: '', cordoba: '', brasil: '', colombia: '', sinOffice: '',
  });

  useEffect(() => {
    if (gov && open) setOffices({ ...EMPTY, ...gov.offices });
  }, [gov, open]);

  const mutation = trpc.officeManager.setGovernance.useMutation({
    onSuccess: () => {
      toast.success(t('office-manager.officemanagergovernancepanel.office_managers_actualizadas'));
      utils.officeManager.getGovernance.invalidate();
      utils.officeManager.getMyAccess.invalidate();
      utils.profile.getVisibility.invalidate();
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const addEmail = (key: OfficeKey) => {
    const email = drafts[key].trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('office-manager.officemanagergovernancepanel.email_invalido'));
      return;
    }
    setOffices((o) => ({ ...o, [key]: o[key].includes(email) ? o[key] : [...o[key], email] }));
    setDrafts((d) => ({ ...d, [key]: '' }));
  };

  const removeEmail = (key: OfficeKey, email: string) =>
    setOffices((o) => ({ ...o, [key]: o[key].filter((e) => e !== email) }));

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('office-manager.officemanagergovernancepanel.office_managers_por_oficina')}</DialogTitle>
          <DialogDescription>
            {t('office-manager.officemanagergovernancepanel.estos_emails_ven_el_panel_de')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <WinclapLoader size={32} className="text-foreground/25" />
          </div>
        ) : (
          <div className="space-y-4">
            {OFFICE_ORDER.map((key) => (
              <div key={key}>
                <Label className="font-semibold">{OFFICE_LABELS[key]}</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {offices[key].length === 0 && (
                    <span className="text-xs text-muted-foreground">{t('office-manager.officemanagergovernancepanel.sin_oms_configuradas')}</span>
                  )}
                  {offices[key].map((email) => (
                    <Badge key={email} variant="secondary" className="gap-1 font-normal">
                      {email}
                      <button onClick={() => removeEmail(key, email)} className="hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    placeholder="email@winclap.com"
                    value={drafts[key]}
                    onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEmail(key);
                      }
                    }}
                    className="h-8"
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addEmail(key)}>
                    Agregar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={mutation.isPending || isLoading} onClick={() => mutation.mutate({ offices })}>
            {mutation.isPending && <WinclapLoader size={16} className="mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
